import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { ShieldCheck, ArrowRight, Zap, Scale, TrendingUp } from "lucide-react";
import AdBanner from "@/components/AdBanner";

const GOAL_ICONS = { fast: <Zap size={14} />, balanced: <Scale size={14} />, max_profit: <TrendingUp size={14} /> };

export default function SharedValuation() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  const [catMap, setCatMap] = useState({});

  useEffect(() => {
    api.get(`/share/${token}`).then((r) => setDoc(r.data)).catch(() => setError(true));
    api.get("/categories").then((r) => {
      const map = {};
      (r.data.categories || []).forEach((c) => { map[c.key] = c; });
      setCatMap(map);
    }).catch(() => {});
  }, [token]);

  if (error) {
    return (
      <main data-testid="shared-invalid" className="bg-white min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-display font-black text-4xl tracking-tighter">{t.shared.invalid}</h1>
          <Link to="/valutazione" className="btn-primary inline-flex mt-8">{t.shared.cta} <ArrowRight size={18} /></Link>
        </div>
      </main>
    );
  }
  if (!doc) return null;

  const { input, result } = doc;
  const catLabel = catMap[input.category]?.[lang] || input.category.replace(/_/g, " ");
  const fmt = (n) => `€${Number(n).toLocaleString(lang === "it" ? "it-IT" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const date = new Date(doc.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <main data-testid="shared-page" className="bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div data-testid="shared-badge" className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2">
          <ShieldCheck size={16} className="text-[#FFCC00]" />
          <span className="font-mono text-[10px] font-bold tracking-widest uppercase">{t.shared.badge}</span>
        </div>

        <div className="mt-6 border border-black">
          <div className="p-8 border-b border-black">
            <span className="overline text-[#002FA7]">{catLabel} · {t.valuator.conditions[input.condition] || input.condition}</span>
            <h1 data-testid="shared-item" className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
              {input.brand} {input.model}
            </h1>
            <div className="font-mono text-xs text-[#737373] uppercase mt-2">{t.shared.date}: {date}</div>
          </div>

          <div className="p-8 bg-[#0A0A0A] text-white">
            <div className="overline text-[#FFCC00]">{t.results.market_value}</div>
            <div data-testid="shared-value" className="font-display font-black text-6xl sm:text-7xl tracking-tighter leading-none mt-2">
              {fmt(result.estimated_market_value)}
            </div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-[#A0A0A0] mt-3">
              {t.results.confidence}: {t.results.conf[result.confidence] || result.confidence}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-black border-t border-black">
            {result.strategies.map((s) => (
              <div key={s.goal} data-testid={`shared-strategy-${s.goal}`} className="p-5">
                <div className="flex items-center gap-2 text-[#002FA7]">
                  {GOAL_ICONS[s.goal]}
                  <span className="font-mono text-[10px] font-bold tracking-widest uppercase">{s.label}</span>
                </div>
                <div className="font-display font-black text-2xl tracking-tighter mt-2">{fmt(s.price)}</div>
              </div>
            ))}
          </div>

          {result.reasoning && (
            <div className="p-8 border-t border-black bg-[#F5F5F5]">
              <span className="overline">{t.results.reasoning}</span>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">{result.reasoning}</p>
            </div>
          )}
        </div>

        <div className="mt-8">
          <AdBanner slot="shared" />
        </div>

        <div className="mt-8 text-center">
          <p className="font-mono text-[10px] tracking-widest uppercase text-[#737373]">{t.shared.by}</p>
          <Link to="/valutazione" data-testid="shared-cta" className="btn-primary inline-flex mt-4">
            {t.shared.cta} <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </main>
  );
}
